export type ServiceItem = {
  title: string;
  summary: string;
  details: string[];
};

export const serviceGroups: { label: string; title: string; description: string; services: ServiceItem[] }[] = [
  {
    label: "Evaluate",
    title: "Understand the whole movement system",
    description: "Care begins with a detailed look at how your history, symptoms, mobility, strength, coordination, and daily demands fit together.",
    services: [
      {
        title: "Multi-system evaluation",
        summary: "A whole-person assessment designed to identify the factors limiting how you move.",
        details: ["Movement and functional analysis", "Clear findings and priorities", "An individualized plan of care"],
      },
      {
        title: "Personalized exercise app and programming",
        summary: "A focused home program supports the work you do in the clinic and helps you practice with clarity.",
        details: ["Stretching, strengthening, and core stabilization", "Neuromuscular re-education, therapeutic activity, and neurodynamic work", "Images, video, and individualized cues", "Secure app support when appropriate"],
      },
    ],
  },
  {
    label: "Restore",
    title: "Hands-on and technology-supported care",
    description: "Your plan may combine skilled hands-on treatment, targeted exercise, and feedback tools when they match your needs and goals.",
    services: [
      {
        title: "Manual therapy",
        summary: "Hands-on strategies may be used to address joint and soft-tissue restrictions and create new movement options.",
        details: ["Joint mobilization", "Joint manipulation when appropriate", "Instrument-assisted soft-tissue mobilization", "Self-management education"],
      },
      {
        title: "Dry needling",
        summary: "Targeted dry needling may be incorporated to address sensitive or overactive muscle tissue as one part of a broader plan.",
        details: ["Trigger-point treatment", "Muscle activation or relaxation goals", "Integrated with movement retraining"],
      },
      {
        title: "Blood-flow restriction therapy",
        summary: "BFR may support strength training at lower external loads for selected rehabilitation and performance goals.",
        details: ["Clinician-supervised application", "Useful in selected post-operative plans", "Progressive strength focus"],
      },
      {
        title: "Additional tools and resources",
        summary: "Education and selected supportive tools may make a movement plan easier to understand and practice.",
        details: ["Anatomy and movement education", "Dynamic taping when appropriate", "Ergonomic tools and practical self-management strategies"],
      },
    ],
  },
  {
    label: "Build",
    title: "Strength, skill, and long-term movement health",
    description: "Rehabilitation is not only about settling symptoms. It is also about building the capacity to meet the demands of the life and activities you value.",
    services: [
      {
        title: "Pilates",
        summary: "Pilates offers varied ways to develop core control, mobility, strength, and coordinated movement.",
        details: ["Rehabilitation-informed training", "In-clinic and home options when appropriate", "A possible bridge from therapy into long-term exercise"],
      },
      {
        title: "Telehealth",
        summary: "Secure virtual visits can help maintain continuity when travel, schedules, or other barriers make an in-clinic visit difficult.",
        details: ["Live movement coaching", "Program review and progression", "Convenient continuity of care"],
      },
      {
        title: "Injury-prevention programs",
        summary: "Movement analysis and objective baselines can reveal opportunities before they become larger barriers.",
        details: ["Right-to-left comparisons", "Movement-quality screening", "Programs for running, lifting, golf, and other demands"],
      },
      {
        title: "Sports performance",
        summary: "Performance work connects physical capacity, movement control, and the specific demands of a sport or activity.",
        details: ["Running and golf programs", "Return-to-sport progressions", "Strength, control, and movement-efficiency goals"],
      },
    ],
  },
];

export const conditionGroups = [
  {
    title: "Everyday and non-operative rehabilitation",
    conditions: [
      "Arthritis", "Balance and walking problems", "Bursitis", "Bulging or herniated discs", "Car-accident injuries",
      "Carpal tunnel syndrome", "Chronic pain", "Fall prevention", "Fracture rehabilitation", "Headaches and migraines",
      "Low-back pain", "Motor-control deficits", "Muscle and joint pain", "Neck pain", "Neuralgia (nerve pain)",
      "Piriformis syndrome", "Plantar fasciitis", "Postural imbalances", "Pregnancy and postpartum pain", "Sacroiliac pain",
      "Sciatica", "Sports injuries", "TMJ (jaw pain and headache)", "Work-related injuries",
    ],
  },
  {
    title: "Orthopedic and tendon conditions",
    conditions: [
      "Adhesive capsulitis (frozen shoulder)", "Ankle sprains", "Golfer’s elbow", "Ligament injuries", "Muscle strains",
      "Rotator-cuff injury or shoulder instability", "Scoliosis and kyphosis", "Spondylolisthesis or spondylosis",
      "Tendinitis and other tendon conditions", "Tennis elbow", "Thoracic outlet syndrome", "Whiplash injuries",
    ],
  },
  {
    title: "Post-operative rehabilitation",
    conditions: [
      "ACL reconstruction", "Achilles-tendon repair", "Total Joint Replacements", "Hip-labral repair",
      "Knee-meniscus repair", "Rotator-cuff repair", "Shoulder-labral repair", "Spinal fusion",
    ],
  },
  {
    title: "Neurologic, balance, and vestibular care",
    conditions: [
      "Balance and gait limitations", "Concussion rehabilitation and vestibular therapy", "Fall-risk reduction", "Vestibular rehabilitation",
    ],
  },
];

export const sports = [
  "Baseball", "Basketball", "Cheerleading", "Cycling", "Dance", "Exercise and general fitness", "Field hockey", "Figure skating", "Football", "Golf",
  "Hockey", "Kickboxing", "Lacrosse", "Pickleball", "Rugby", "Running", "Skiing", "Snowboarding", "Soccer", "Squash",
  "Swimming", "Tennis", "Volleyball", "Weightlifting", "Wrestling",
];

export const serviceAreas = [
  "Overland Park", "Lenexa", "Leawood", "Olathe", "Shawnee", "Prairie Village", "Mission", "Merriam", "Fairway",
  "Mission Hills", "Roeland Park", "Westwood", "Lawrence", "Kansas City, Kansas", "Grandview", "Kansas City, Missouri",
  "Lee’s Summit", "Martin City", "Parkville", "Raytown",
];
